import React, { useState, useEffect, useRef } from "react";
import { Box, Button, CircularProgress, IconButton } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { Clear as ClearIcon } from "@mui/icons-material";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import config from "../../config.js";
import DashboardUpcomingCards from "./DashboardUpcomingCards";
import "./Dashboard.css";
import {
  getUserTimeFormat,
  getUserDateFormat,
  convertToDateFnsFormat,
} from "../../utils/formatDate.js";

/**
 * DashboardUpcomingTrip Component
 * Displays schedules for a selected date/time or today's schedules if no date is selected
 * Supports recurring schedules (DAILY, WEEKLY, MONTHLY)
 * Supports internationalization
 * Enhanced with time selection functionality
 */
export default function DashboardUpcomingTrip() {
  const { t } = useTranslation();
  const parsedData = JSON.parse(localStorage.getItem("userState"));
  const orgId = parsedData?.user?.organizationId;
  const userTimeFormat = getUserTimeFormat();
  const userDateFormat = getUserDateFormat();
  const is24Hour = userTimeFormat === "24hr";
  const timePlaceholder = is24Hour ? "--:--" : "--:-- --";

  const getDateInputFormat = () => {
    return convertToDateFnsFormat(userDateFormat);
  };
  const navigate = useNavigate();
  const [schedules, setSchedules] = useState([]);
  const [filteredSchedules, setFilteredSchedules] = useState([]);
  const [selectedDateTime, setSelectedDateTime] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const dateTimePickerRef = useRef(null);

  const handleApiError = (error) => {
    console.error("API Error:", error);
    if (error.response?.status === 401) {
      navigate("/");
    }
  };

  const getAllSchedules = async () => {
    if (!orgId) {
      return [];
    }

    try {
      const resp = await axios.get(`${config.baseURL}/schedule/organization`, {
        params: { organizationId: orgId },
        withCredentials: true,
      });
      return resp.data;
    } catch (error) {
      handleApiError(error);
      return [];
    }
  };

  const getSelectedDateTime = () => {
    return selectedDateTime ? new Date(selectedDateTime) : new Date();
  };

  const getValidDate = (value) => {
    if (!value) {
      return null;
    }

    const parsedDate = value instanceof Date ? value : new Date(value);
    return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
  };

  const getRelevantOccurrenceWindow = (schedule) => {
    if (!schedule || schedule.scheduleStatus === "COMPLETED") {
      return null;
    }

    if (
      schedule.scheduleStatus === "RUNNING" &&
      schedule.currentOccurrence?.start
    ) {
      return schedule.currentOccurrence;
    }

    if (
      schedule.scheduleStatus === "SCHEDULED" &&
      schedule.nextOccurrence?.start
    ) {
      return schedule.nextOccurrence;
    }

    if (schedule.nextOccurrence?.start) {
      return schedule.nextOccurrence;
    }

    if (schedule.currentOccurrence?.start) {
      return schedule.currentOccurrence;
    }

    if (schedule.startTime) {
      return {
        start: schedule.startTime,
        end: schedule.endTime || null,
      };
    }

    return null;
  };

  const normalizeScheduleForDashboard = (schedule) => {
    const occurrenceWindow = getRelevantOccurrenceWindow(schedule);
    const occurrenceStart = getValidDate(occurrenceWindow?.start);
    const occurrenceEnd =
      getValidDate(occurrenceWindow?.end) || getValidDate(schedule?.endTime);

    if (!occurrenceStart) {
      return null;
    }

    return {
      ...schedule,
      startTime: occurrenceStart.toISOString(),
      endTime: occurrenceEnd
        ? occurrenceEnd.toISOString()
        : schedule?.endTime || null,
      dashboardOccurrenceStart: occurrenceStart,
      dashboardOccurrenceEnd: occurrenceEnd,
    };
  };

  const occursOnDate = (schedule, targetDate) => {
    const occurrenceStart = schedule?.dashboardOccurrenceStart;
    const occurrenceEnd =
      schedule?.dashboardOccurrenceEnd || schedule?.dashboardOccurrenceStart;

    if (!occurrenceStart || !targetDate) {
      return false;
    }

    const dayStart = new Date(
      targetDate.getFullYear(),
      targetDate.getMonth(),
      targetDate.getDate(),
      0,
      0,
      0,
      0
    );
    const dayEnd = new Date(
      targetDate.getFullYear(),
      targetDate.getMonth(),
      targetDate.getDate(),
      23,
      59,
      59,
      999
    );

    return occurrenceStart <= dayEnd && occurrenceEnd >= dayStart;
  };

  const sortSchedulesByProximity = (
    schedules,
    referenceTime,
    isSelectedDateTime = false
  ) => {
    const referenceTimestamp = referenceTime.getTime();

    const schedulesWithProximity = schedules.map((schedule) => {
      const scheduleStartTime =
        schedule?.dashboardOccurrenceStart?.getTime() ||
        new Date(schedule.startTime).getTime();
      const scheduleEndTime =
        schedule?.dashboardOccurrenceEnd?.getTime() || scheduleStartTime;
      const timeDiff = Math.abs(scheduleStartTime - referenceTimestamp);
      const isBefore = scheduleStartTime < referenceTimestamp;
      const isRunning =
        scheduleEndTime >= referenceTimestamp && scheduleStartTime <= referenceTimestamp;

      return {
        ...schedule,
        timeDiff,
        isBefore,
        isRunning,
        scheduleEndTime,
        scheduleStartTime,
      };
    });

    if (isSelectedDateTime) {
      return schedulesWithProximity.sort((a, b) => {
        if (a.isBefore !== b.isBefore) {
          return a.isBefore ? 1 : -1;
        }
        return a.timeDiff - b.timeDiff;
      });
    } else {
      return schedulesWithProximity.sort((a, b) => {
        if (a.isRunning !== b.isRunning) {
          return a.isRunning ? -1 : 1;
        }

        if (a.isRunning && b.isRunning) {
          return a.timeDiff - b.timeDiff;
        }

        return a.scheduleStartTime - b.scheduleStartTime;
      });
    }
  };

  const handleViewAllTrips = () => {
    navigate("/schedule");
  }

  const filterSchedulesByDateTime = (schedules, selectedDateTime) => {
    const hasSelectedDateTime = selectedDateTime !== null;
    const referenceTime = hasSelectedDateTime
      ? getSelectedDateTime()
      : new Date();

    if (Number.isNaN(referenceTime.getTime())) {
      return [];
    }

    const normalizedSchedules = schedules
      .map(normalizeScheduleForDashboard)
      .filter(Boolean);

    const relevantSchedules = hasSelectedDateTime
      ? normalizedSchedules.filter((schedule) => {
          const targetDate = new Date(
            referenceTime.getFullYear(),
            referenceTime.getMonth(),
            referenceTime.getDate()
          );
          return occursOnDate(schedule, targetDate);
        })
      : normalizedSchedules.filter((schedule) => {
          const occurrenceEnd =
            schedule?.dashboardOccurrenceEnd || schedule?.dashboardOccurrenceStart;

          return occurrenceEnd && occurrenceEnd.getTime() >= referenceTime.getTime();
        });

    const sortedSchedules = sortSchedulesByProximity(
      relevantSchedules,
      referenceTime,
      hasSelectedDateTime
    );

    return sortedSchedules;
  };

  const handleDateTimeChange = (newValue) => {
    setSelectedDateTime(newValue);
  };

  const clearSelectedDateTime = () => {
    setSelectedDateTime(null);
  };

  useEffect(() => {
    let isMounted = true;

    const fetchSchedules = async () => {
      setIsLoading(true);
      try {
        const data = await getAllSchedules();
        if (isMounted && data) {
          setSchedules(data);
          const filtered = filterSchedulesByDateTime(data, selectedDateTime);
          setFilteredSchedules(filtered);
        }
      } catch (error) {
        console.error("Error fetching schedules:", error);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchSchedules();

    const intervalId = setInterval(() => {
      fetchSchedules();
    }, 5 * 60 * 1000);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [orgId]);

  useEffect(() => {
    if (schedules.length > 0) {
      const filtered = filterSchedulesByDateTime(schedules, selectedDateTime);
      setFilteredSchedules(filtered);
    }
  }, [schedules, selectedDateTime]);

  useEffect(() => {
    if (!selectedDateTime && schedules.length > 0) {
      const intervalId = setInterval(() => {
        const filtered = filterSchedulesByDateTime(schedules, selectedDateTime);
        setFilteredSchedules(filtered);
      }, 60000);

      return () => clearInterval(intervalId);
    }
  }, [schedules, selectedDateTime]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box className="upcoming-trips">
        <div className="upcoming-title">
          <span className="upcoming-title">
            {t("upcomingTrips", "Upcoming Trips")}
          </span>
          <div
            // className="datetime-filter-container"
            style={{ display: "flex", alignItems: "center", gap: "10px" }}
          >
            <div
              style={{
                // border: "2px solid #183059",
                borderWidth: "2px",
                display: "flex",
                alignItems: "center",
                gap: "5px",
                borderRadius: "10px",
              }}
            >
              {/* <div
                ref={dateTimePickerRef}
                style={{
                  display: "flex",
                  alignItems: "center",
                  //  gap: "5px"
                }}
              >
                <DatePicker
                  value={selectedDateTime}
                  onChange={handleDateTimeChange}
                  format={getDateInputFormat()}
                  slotProps={{
                    textField: {
                      placeholder: t("today", "Today"),
                      autoFocus: false,
                      sx: {
                        maxWidth: "190px",
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "10px",
                          fontSize: "0.9rem",
                          paddingRight: "8px",
                          paddingTop: 0,
                          paddingBottom: 0,
                          "& input": {
                            padding: "10.5px 14px",
                            fontSize: "0.9rem",
                          },
                          "& fieldset": {
                            border: "none",
                            // borderColor: "#183059",
                            // borderWidth: "2px",
                          },
                        },
                        "& .MuiInputAdornment-root": {
                          marginTop: 0,
                          marginBottom: 0,
                          marginLeft: "-6px",
                        },
                        // "& .MuiSvgIcon-root": {
                        //   fontSize: "20px",
                        //   verticalAlign: "middle",
                        // },
                      },
                    },
                  }}
                />

                <TimePicker
                  value={selectedDateTime}
                  onChange={handleDateTimeChange}
                  ampm={!is24Hour}
                  slotProps={{
                    textField: {
                      placeholder: timePlaceholder,
                      autoFocus: false,
                      sx: {
                        maxWidth: "90px",
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "10px",
                          fontSize: "0.9rem",
                          paddingRight: "8px",
                          marginTop: 0, // ← ADD THIS
                          marginBottom: 0,
                          paddingTop: 0,
                          paddingBottom: 0,
                          "& input": {
                            padding: "10.5px 14px",
                            fontSize: "0.9rem",
                          },
                          "& fieldset": {
                            border: "none",
                            // borderWidth: "2px",
                          },
                        },
                      },
                    },
                    openPickerIcon: { sx: { display: "none" } },
                  }}
                />

                {selectedDateTime && (
                  <IconButton
                    onClick={clearSelectedDateTime}
                    sx={{
                      color: "#999",
                      marginTop: 0, // ← Change from "10px" to 0
                      marginBottom: 0,
                      width: "24px",
                      height: "24px",
                    }}
                    title={t("clearDateTime", "Clear date and time")}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                )}
              </div> */}
              <Button
                sx={{
                  fontFamily: "Roboto",
                  fontWeight: 600,
                  fontSize: "16px",
                  color: "#FFFFFF",
                  padding: "0px 16px",
                  marginRight: "8px",
                  minWidth: "80px",
                  height: "40px",
                  borderRadius: "8px",
                  textTransform: "none",
                  backgroundColor: "#20A8E0",
                  borderRight: "1px solid #e0e0e0",
                  "&:hover": {
                    backgroundColor: "#20A8E0",
                  },
                }}
                onClick={handleViewAllTrips}
              >
                {t("View All")}
              </Button>
            </div>
          </div>
        </div>

        <div className="upcoming-data">
          {isLoading ? (
            <div className="loading-container">
              <CircularProgress size={20} />
              <span>{t("loading", "Loading...")}</span>
            </div>
          ) : (
            <>
              <DashboardUpcomingCards schedules={filteredSchedules} />
              {/* {filteredSchedules.length === 0 && (
                                <div
                                    className="no-schedules-message"
                                    style={{
                                        textAlign: 'center',
                                        padding: '20px',
                                        color: '#666',
                                        fontSize: '16px',
                                    }}
                                >
                                    {selectedDateTime
                                        ? t('noTripsForDateTime', 'No trips scheduled for selected date and time')
                                        : t('noUpcomingTrips', 'No upcoming trips scheduled')
                                    }
                                </div>
                            )} */}
            </>
          )}
        </div>
      </Box>
    </LocalizationProvider>
  );
}
